/**
 * CP2Waiting — waiting for partner to confirm.
 * data-screen-label="cp-02 Partner waiting"
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../chrome/AppHeader';
import { SecondaryButton } from '../../chrome/Button';
import { CPBackdrop, CPStars } from './shared';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

interface CP2WaitingProps {
  flow: UseCoupleFlowReturn;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSecs = Math.floor(ms / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function CP2Waiting({ flow }: CP2WaitingProps): JSX.Element {
  const { t } = useTranslation('flows');
  const expiresAt = flow.expiresAt;
  const [remaining, setRemaining] = useState<number>(() => {
    if (!expiresAt) return 0;
    return new Date(expiresAt).getTime() - Date.now();
  });
  const [expired, setExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!expiresAt) return;

    function tick(): void {
      const ms = new Date(expiresAt!).getTime() - Date.now();
      if (ms <= 0) {
        setRemaining(0);
        setExpired(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        setRemaining(ms);
      }
    }

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [expiresAt]);

  // Auto-reset to CP1 when expired
  useEffect(() => {
    if (expired) {
      const t = setTimeout(() => flow.send({ type: 'RESET' }), 3000);
      return () => clearTimeout(t);
    }
  }, [expired, flow]);

  function handleCancel(): void {
    void flow.cancel();
  }

  return (
    <div className={styles.root} data-screen-label="cp-02 Partner waiting">
      <CPBackdrop tone="quiet" />
      <CPStars count={30} />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('couple.headerTitle')} onBack={() => void flow.cancel()} headingId="couple-heading" />

        {/* Title */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{t('couple.waiting.eyebrow')}</div>
          <h1 className={styles.displayTitleCenter}>
            {t('couple.waiting.titlePrefix')} <em className={styles.gradAccent}>{t('couple.waiting.titleAccent')}</em>
          </h1>
        </div>

        {/* Partner username */}
        {flow.partnerUsername && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'center',
              fontSize: 'var(--type-ui)',
              color: 'var(--amber)',
              fontWeight: 600,
              letterSpacing: '0.12em',
            }}
          >
            @{flow.partnerUsername}
          </div>
        )}

        {/* Countdown */}
        <div className={styles.countdownWrap}>
          <span className={styles.countdownLabel}>{t('couple.waiting.expiresLabel')}</span>
          {expired ? (
            <div className={styles.countdownExpired}>
              {t('couple.waiting.expired')}
            </div>
          ) : (
            <div className={styles.countdownTimer}>
              {formatCountdown(remaining)}
            </div>
          )}
        </div>

        {/* Cancel */}
        <div className={styles.ctaArea}>
          <SecondaryButton
            type="button"
            onClick={handleCancel}
          >
            {t('couple.waiting.cancelCta')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
